import axios from 'axios';
import { loadAgentId, saveAgentId } from '@/utils/saleCache';
import { hasTravelPremium, normalizeTravelQuoteApi } from '@/utils/travelPricing';
import { travelPaymentReference } from '@/utils/travelPaymentReference';

function parseAgentNumericCode(value) {
  if (value == null || value === '') return null;
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? null : parsed;
}

const TRAVEL_BASE = '/customer/quotation/travel';
const MAPFRE_BASE = '/customer/gis/mapfre/policy';
const MAPFRE_AUTH_BASE = '/customer/gis/mapfre/auth';
const PAYMENT_BASE = '/customer/payment/client-api/v1/payments/mpesa';
const MSG_LOAD_TRAVEL_QUOTES = 'Failed to load travel quotes';

/** Agent travel quotes via quotations-api (Mapfre pricing still from gis-apis). */
export const AGENT_TRAVEL_QUOTES_API_ENABLED = true;

export function isRemoteTravelQuoteId(quoteId) {
  const id = String(quoteId || '').trim();
  if (!/^\d+$/.test(id)) return false;
  // Local fallback IDs use Date.now() (13-digit ms timestamp); API quote_ids are short integers.
  return id.length <= 9;
}

export function generateLocalTravelQuoteId() {
  return String(Date.now());
}

export { travelPaymentReference };

/** Resolve the customer ID used for travel pay account (prefer national ID, then passport digits). */
export function resolveTravelCustomerIdNo(source = {}) {
  // Legacy RFQ forms still send snake_case id_no.
  // eslint-disable-next-line camelcase
  const legacyIdNo = source.id_no;
  return source.idNumber || source.nationalId || source.idNo || legacyIdNo || source.passport || '';
}

function quotesApiSkipped() {
  return { success: true, skipped: true };
}

function quotesApiUnavailable() {
  return {
    success: false, skipped: true, local: true, message: 'Quote stored in session only',
  };
}

/** Human-readable text from quotations-api Result { success, msg, data }. */
function quotationResultMessage(body, fallback = 'Request failed') {
  if (!body || typeof body !== 'object') return fallback;
  const parts = [body.msg, body.message, body.error]
    .filter((v) => typeof v === 'string' && v.trim())
    .map((v) => v.trim());
  const dataText = typeof body.data === 'string' ? body.data.trim() : '';
  if (dataText && !parts.includes(dataText)) parts.push(dataText);
  const unique = [...new Set(parts.filter(Boolean))];
  return unique.join('. ') || fallback;
}

function parseQuotationResult(body, { successFallback, failFallback } = {}) {
  if (!body || typeof body !== 'object') {
    return { success: false, message: failFallback || 'Unexpected response' };
  }
  if (body.success) {
    const message = quotationResultMessage(body, successFallback || 'Success');
    const msg = typeof body.msg === 'string' ? body.msg.trim() : '';
    const data = typeof body.data === 'string' ? body.data.trim() : '';
    const alreadyPaid = /already paid/i.test(msg)
      || /already applied/i.test(data)
      || /already paid/i.test(message);
    return {
      success: true, message, alreadyPaid, data: body.data,
    };
  }
  return {
    success: false,
    message: quotationResultMessage(body, failFallback || 'Request failed'),
  };
}

function fail(err, fallback) {
  const body = err?.response?.data;
  if (body) {
    return { success: false, message: quotationResultMessage(body, fallback) };
  }
  return { success: false, message: err?.message || fallback };
}

function unwrap(res) {
  const body = res?.data;
  if (body && typeof body.success === 'boolean') return body;
  return { success: true, data: body };
}

function extractTravelQuoteDto(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.quoteId != null || payload.premiumUsd != null || payload.kshAmount != null) {
    return payload;
  }
  if (payload.data && typeof payload.data === 'object') {
    return extractTravelQuoteDto(payload.data);
  }
  return null;
}

function mapQuoteView(dto) {
  if (!dto) return null;
  return {
    quoteId: dto.quoteId,
    prodCode: dto.prodCode,
    productLabel: dto.productLabel,
    region: dto.region,
    zone: dto.zone,
    planType: dto.planType,
    destinationCountry: dto.destinationCountry,
    startDate: dto.travelStartDate,
    endDate: dto.travelEndDate,
    travel_start_date: dto.travelStartDate,
    travel_end_date: dto.travelEndDate,
    duration: dto.durationLabel,
    travellerCount: dto.travellerCount,
    traveller_count: dto.travellerCount,
    grossUsd: dto.premiumUsd,
    premiumUsd: dto.premiumUsd,
    premium: dto.premiumUsd,
    fxRate: dto.fxRate,
    fx_rate: dto.fxRate,
    kshAmount: dto.kshAmount,
    ksh_amount: dto.kshAmount,
    email_address: dto.email,
    customerEmail: dto.email,
    phone: dto.phone,
    policyNumber: dto.policyNumber,
    policy_number: dto.policyNumber,
    tqPolicyNumber: dto.tqPolicyNumber,
    tqBatchNo: dto.tqBatchNo,
    paymentStatus: dto.paymentStatus,
    status: dto.status,
    journey: dto.journey,
    plan_type: dto.planType,
    destination_country: dto.destinationCountry,
    paymentReference: dto.paymentReference,
    travellersJson: dto.travellersJson,
    quoteTravellersJson: dto.quoteTravellersJson,
    mainTravellerDob: dto.mainTravellerDob,
    agnShtDesc: dto.agnShtDesc,
    stepCode: dto.stepCode,
  };
}

function mapCreatePayload(quoteData, agentId) {
  return {
    prodCode: quoteData.prod_code || quoteData.prodCode,
    productLabel: quoteData.productLabel || quoteData.plan_type,
    region: quoteData.region,
    zone: quoteData.zone,
    planType: quoteData.plan_type || quoteData.planType,
    destinationCountry: quoteData.destination_country || quoteData.destinationCountry,
    travelStartDate: quoteData.travel_start_date || quoteData.startDate,
    travelEndDate: quoteData.travel_end_date || quoteData.endDate,
    durationLabel: quoteData.duration || quoteData.durationLabel,
    travellerCount: quoteData.traveller_count || quoteData.travellerCount,
    premiumUsd: quoteData.premium || quoteData.grossUsd || quoteData.revised_price,
    fxRate: quoteData.fx_rate || quoteData.fxRate,
    kshAmount: quoteData.ksh_amount || quoteData.kshAmount,
    email: quoteData.email_address || quoteData.customerEmail,
    clientFullName: quoteData.client_full_name || quoteData.clientFullName,
    agnShtDesc: quoteData.agnShtDesc,
    journey: quoteData.agent_journey || quoteData.journey || 'quote',
    agentId,
    mainTravellerDob: quoteData.birth_date || quoteData.mainTravellerDob,
    quoteTravellersJson: quoteData.quoteTravellersJson
      || (Array.isArray(quoteData.quoteTravellers)
        ? JSON.stringify(quoteData.quoteTravellers)
        : undefined),
  };
}

const TOKEN_TTL_MS = 18 * 60 * 1000;
let cachedMapfreToken = null;
let tokenFetchedAt = 0;

function extractToken(body) {
  return body?.data?.token || body?.token || null;
}

async function fetchMapfreToken() {
  const now = Date.now();
  if (cachedMapfreToken && (now - tokenFetchedAt) < TOKEN_TTL_MS) {
    return cachedMapfreToken;
  }

  const serviceRes = await axios.post(
    `${MAPFRE_AUTH_BASE}/mapfre-service-login`,
    {},
    { validateStatus: () => true, _skipErrorRedirect: true },
  );
  if (serviceRes.status === 200 && serviceRes.data?.success) {
    const token = extractToken(serviceRes.data);
    if (token) {
      cachedMapfreToken = token;
      tokenFetchedAt = now;
      return token;
    }
  }

  const user = process.env.VUE_APP_MAPFRE_USER;
  const pass = process.env.VUE_APP_MAPFRE_PASS;
  if (user && pass) {
    const loginRes = await axios.post(
      `${MAPFRE_AUTH_BASE}/login`,
      { user, pass, country: 50 },
      { validateStatus: () => true, _skipErrorRedirect: true },
    );
    if (loginRes.status === 200 && loginRes.data?.success) {
      const token = extractToken(loginRes.data);
      if (token) {
        cachedMapfreToken = token;
        tokenFetchedAt = now;
        return token;
      }
    }
  }

  const msg = serviceRes.data?.message || 'Mapfre login failed';
  throw new Error(msg);
}

async function mapfreAuthConfig() {
  const token = await fetchMapfreToken();
  return {
    headers: { Authorization: `Bearer ${token}` },
    _skipErrorRedirect: true,
  };
}

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return `254${digits.slice(1)}`;
  if (digits.startsWith('254')) return digits;
  if (digits.startsWith('7') && digits.length === 9) return `254${digits}`;
  return digits;
}

/** True when quote is still unpaid / in-progress (Activity resume). */
export function isTravelOpenQuote(quote) {
  if (quote?.quoteId == null && quote?.id == null) return false;
  const policyNo = String(quote.policyNumber || quote.policy_number || '').trim();
  const paid = quote.paymentStatus === true
    || quote.paymentStatus === 1
    || quote.status === 'PAID';
  if (paid && policyNo) return false;
  return true;
}

function mapTravelClientPolicy(dto) {
  if (!dto) return null;
  return {
    quoteId: dto.quoteId,
    mapfrePolicyNumber: dto.mapfrePolicyNumber,
    tqPolicyNumber: dto.tqPolicyNumber,
    tqBatchNo: dto.tqBatchNo,
    prodCode: dto.prodCode,
    productLabel: dto.productLabel,
    region: dto.region,
    destinationCountry: dto.destinationCountry,
    travelStartDate: dto.travelStartDate,
    travelEndDate: dto.travelEndDate,
    durationLabel: dto.durationLabel,
    travellerCount: dto.travellerCount,
    premiumKsh: dto.premiumKsh,
    proposerName: dto.proposerName,
  };
}

export async function listTravelClientPolicies({ email } = {}) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail) {
    return { success: false, message: 'Email is required' };
  }
  try {
    const res = await axios.get(`${TRAVEL_BASE}/policies/client`, {
      params: { email: normalizedEmail, view: 'issued' },
      _skipErrorRedirect: true,
    });
    const body = unwrap(res);
    if (!body.success) {
      return { success: false, message: body.msg || body.message || 'Failed to load travel policies' };
    }
    const rows = Array.isArray(body.data) ? body.data.map(mapTravelClientPolicy).filter(Boolean) : [];
    return { success: true, data: rows };
  } catch (e) {
    return fail(e, 'Failed to load travel policies');
  }
}

export async function listTravelAgentQuotes(agentId, page = 0, size = 20) {
  const normAgentId = String(agentId || '').trim();
  if (!normAgentId) {
    return { success: false, message: 'Agent ID is required' };
  }
  try {
    const res = await axios.get(`${TRAVEL_BASE}/agent/quotes/${encodeURIComponent(normAgentId)}`, {
      params: { page, size },
      _skipErrorRedirect: true,
    });
    const body = unwrap(res);
    if (!body.success && body.success !== undefined) {
      return { success: false, message: body.msg || body.message || MSG_LOAD_TRAVEL_QUOTES };
    }
    const rawList = body.data?.content ?? body.content ?? body.data ?? (Array.isArray(body) ? body : []);
    const rows = Array.isArray(rawList)
      ? rawList.map((row) => mapQuoteView(row)).filter(Boolean)
      : [];
    return { success: true, data: rows.filter((row) => isTravelOpenQuote(row)) };
  } catch (e) {
    return fail(e, MSG_LOAD_TRAVEL_QUOTES);
  }
}

export async function listTravelClientQuotes({ email } = {}) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail) {
    return { success: false, message: 'Email is required' };
  }
  try {
    const res = await axios.get(`${TRAVEL_BASE}/policies/client`, {
      params: { email: normalizedEmail, view: 'open' },
      _skipErrorRedirect: true,
    });
    const body = unwrap(res);
    if (!body.success) {
      return { success: false, message: body.msg || body.message || MSG_LOAD_TRAVEL_QUOTES };
    }
    const rows = Array.isArray(body.data)
      ? body.data.map((row) => mapQuoteView(row)).filter(Boolean)
      : [];
    return { success: true, data: rows.filter((row) => isTravelOpenQuote(row)) };
  } catch (e) {
    return fail(e, MSG_LOAD_TRAVEL_QUOTES);
  }
}

const agentTravelApi = {
  async getAvailableProducts() {
    try {
      const res = await axios.get(`${MAPFRE_BASE}/products/available`, { _skipErrorRedirect: true });
      return unwrap(res);
    } catch (e) {
      return fail(e, 'Failed to load travel products');
    }
  },

  async getTravelFxRate() {
    if (!AGENT_TRAVEL_QUOTES_API_ENABLED) {
      return { success: true, data: { rate: 130 } };
    }
    try {
      const res = await axios.get(`${TRAVEL_BASE}/fx-rate`, {
        _skipErrorRedirect: true,
        timeout: 8000,
      });
      const body = unwrap(res);
      if (body.success && body.data) {
        return { success: true, data: body.data };
      }
      return { success: false, message: quotationResultMessage(body, 'Failed to load FX rate') };
    } catch (e) {
      return fail(e, 'Failed to load FX rate');
    }
  },

  async getPrice(payload) {
    try {
      let authConfig = { _skipErrorRedirect: true };
      try {
        authConfig = await mapfreAuthConfig();
      } catch {
        // gis-apis can authenticate server-side when no Bearer token is sent
      }
      const res = await axios.post(`${MAPFRE_BASE}/getPrice`, payload, authConfig);
      return unwrap(res);
    } catch (e) {
      return fail(e, 'Failed to calculate price');
    }
  },

  async createQuote(quoteData, agentId) {
    if (!AGENT_TRAVEL_QUOTES_API_ENABLED) {
      return { success: true, data: { id: generateLocalTravelQuoteId() }, local: true };
    }
    try {
      const res = await axios.post(
        `${TRAVEL_BASE}/create`,
        mapCreatePayload(quoteData, agentId),
        { _skipErrorRedirect: true },
      );
      const body = unwrap(res);
      if (body.success && body.data) {
        return { success: true, data: { id: body.data.quoteId, ...mapQuoteView(body.data) } };
      }
      return { success: false, message: body.msg || body.message || 'Failed to save quote' };
    } catch (e) {
      return fail(e, 'Failed to save quote');
    }
  },

  async updateQuotePricing(quoteId, pricingData) {
    if (!AGENT_TRAVEL_QUOTES_API_ENABLED || !isRemoteTravelQuoteId(quoteId)) {
      return quotesApiSkipped();
    }
    try {
      const res = await axios.post(
        `${TRAVEL_BASE}/pricing/sync`,
        {
          quoteId: Number(quoteId),
          mapfrePricingJson: JSON.stringify(pricingData || {}),
        },
        { _skipErrorRedirect: true },
      );
      const body = unwrap(res);
      if (body.success && body.data) {
        return { success: true, data: mapQuoteView(body.data) };
      }
      return { success: false, message: body.msg || body.message || 'Failed to update quote pricing' };
    } catch (e) {
      return fail(e, 'Failed to update quote pricing');
    }
  },

  async updateQuoteDetails(quoteId, details) {
    if (!AGENT_TRAVEL_QUOTES_API_ENABLED || !isRemoteTravelQuoteId(quoteId)) {
      return quotesApiSkipped();
    }
    try {
      const travellers = details.travellers || [];
      const clientFullName = [details.first_name, details.last_name]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
      const payload = {
        quoteId: Number(quoteId),
        email: details.email_address || details.customerEmail,
        clientFullName: clientFullName || details.clientFullName,
        phone: details.phone_number || details.phone,
        travellersJson: travellers.length ? JSON.stringify(travellers) : undefined,
        mapfreIssuePayloadJson: details.mapfreIssuePayloadJson,
        premiumUsd: details.premium || details.revised_price,
        fxRate: details.fx_rate,
        kshAmount: details.ksh_amount,
        stepCode: details.stepCode,
      };
      const res = await axios.put(`${TRAVEL_BASE}/update/agent`, payload, { _skipErrorRedirect: true });
      const body = unwrap(res);
      if (body.success && body.data) {
        return { success: true, data: mapQuoteView(body.data) };
      }
      return { success: false, message: body.msg || body.message || 'Failed to update quote' };
    } catch (e) {
      return fail(e, 'Failed to update quote');
    }
  },

  async markReadyForPayment(quoteId) {
    if (!AGENT_TRAVEL_QUOTES_API_ENABLED || !isRemoteTravelQuoteId(quoteId)) {
      return quotesApiSkipped();
    }
    try {
      const res = await axios.post(
        `${TRAVEL_BASE}/payment/ready`,
        { quoteId: Number(quoteId) },
        { _skipErrorRedirect: true },
      );
      return unwrap(res);
    } catch (e) {
      return fail(e, 'Failed to prepare quote for payment');
    }
  },

  async getQuote(quoteId) {
    if (!AGENT_TRAVEL_QUOTES_API_ENABLED || !isRemoteTravelQuoteId(quoteId)) {
      return quotesApiUnavailable();
    }
    const loadFailedMsg = 'Failed to load quote';
    try {
      const res = await axios.get(`${TRAVEL_BASE}/agent`, {
        params: { quoteId },
        timeout: 15000,
        _skipErrorRedirect: true,
      });
      const body = unwrap(res);
      const raw = extractTravelQuoteDto(body.data) || extractTravelQuoteDto(body);
      if (!raw) {
        return { success: false, message: body.msg || body.message || loadFailedMsg };
      }
      if (body.success === false) {
        return { success: false, message: body.msg || body.message || loadFailedMsg };
      }
      const data = normalizeTravelQuoteApi(raw);
      if (!hasTravelPremium(data)) {
        return { success: false, message: 'Quote found but premium is not set yet' };
      }
      return { success: true, data };
    } catch (e) {
      return fail(e, loadFailedMsg);
    }
  },

  async emailTravelQuote() {
    return { success: false, skipped: true, message: 'Use client email fallback' };
  },

  async requestTravelStkPush({
    quoteId,
    payableAmount,
    customerPhoneNumber,
    transactionReference,
    transactionDescription,
    customerIdNo,
    agentId,
    channel = 'agent',
  }) {
    const phone = normalizePhone(customerPhoneNumber);
    if (phone.length < 12) {
      return { success: false, message: 'Enter a valid M-Pesa phone number' };
    }
    const ref = transactionReference
      || travelPaymentReference(resolveTravelCustomerIdNo({ idNumber: customerIdNo }));
    if (!ref) {
      return { success: false, message: 'Customer ID number is required for payment account reference' };
    }
    const endpoint = channel === 'customer'
      ? `${PAYMENT_BASE}/stk/request`
      : `${PAYMENT_BASE}/stk/request/agent`;
    const payload = {
      serviceType: 'TRAVEL',
      settlementProduct: 'TRAVEL_MAPFRE',
      quoteId: Number(quoteId),
      transactionType: 'PAYMENT',
      payableAmount: Math.max(1, Math.round(Number(payableAmount) || 0)),
      transactionReference: ref,
      transactionDescription: (transactionDescription || `TRAVEL ${quoteId}`).slice(0, 13),
      customerPhoneNumber: phone,
    };
    if (channel === 'agent') {
      payload.agentId = agentId;
    }
    try {
      const res = await axios.post(endpoint, payload, { _skipErrorRedirect: true });
      return unwrap(res);
    } catch (e) {
      return fail(e, 'M-Pesa request failed');
    }
  },

  async confirmTravelPaybillPayment({ quoteId, accountNumber, customerIdNo }) {
    try {
      const res = await axios.post(
        `${TRAVEL_BASE}/payment/paybill-confirm`,
        {
          quoteId: Number(quoteId),
          accountNumber: accountNumber
            || travelPaymentReference(resolveTravelCustomerIdNo({ idNumber: customerIdNo })),
        },
        {
          validateStatus: () => true,
          _skipErrorRedirect: true,
        },
      );
      return parseQuotationResult(res?.data, {
        successFallback: 'Payment confirmed. Your policy will be issued shortly.',
        failFallback: 'Payment not yet received. Complete Paybill and try again.',
      });
    } catch (e) {
      return fail(e, 'Payment confirmation failed');
    }
  },

  async pollPaymentStatus(paymentId, channel = 'agent') {
    try {
      const queryPath = channel === 'customer'
        ? `${PAYMENT_BASE}/payment/query`
        : `${PAYMENT_BASE}/payment/query/agent`;
      const res = await axios.get(
        `${queryPath}?paymentId=${paymentId}`,
        { _skipErrorRedirect: true },
      );
      return unwrap(res);
    } catch (e) {
      return fail(e, 'Payment status check failed');
    }
  },

  async resolveAgentShortCode(user) {
    const email = user?.email || '';
    const agnCode = user?.['custom:agent_code'];
    if (!agnCode && !email) return 'DIRECT';
    try {
      const baseUrl = '/customer/gis/agent/dashboard/summary';
      const url = agnCode
        ? `${baseUrl}?agn_code=${agnCode}`
        : `${baseUrl}?email=${encodeURIComponent(email)}`;
      const res = await axios.get(url, {
        validateStatus: () => true,
        timeout: 10000,
        _skipErrorRedirect: true,
      });
      if (res.status === 200 && res.data) {
        const shortDesc = res.data.shortDesc
          || res.data.agent?.shortDesc
          || res.data.summary?.shortDesc;
        if (shortDesc) return String(shortDesc);
      }
    } catch (e) {
      // fall through
    }
    return agnCode ? String(agnCode) : 'DIRECT';
  },

  async resolveAgentNumericId(user) {
    const email = user?.email || '';
    const agnCode = user?.['custom:agent_code'];

    try {
      const fromDashboard = parseAgentNumericCode(localStorage.getItem('agentNumericId'));
      if (fromDashboard != null) return fromDashboard;
    } catch (e) {
      // ignore storage errors
    }

    if (email) {
      const cached = loadAgentId(email);
      if (cached != null) return cached;
    }

    const fromCognito = parseAgentNumericCode(agnCode);
    if (fromCognito != null) return fromCognito;

    if (!agnCode && !email) return 0;

    try {
      const baseUrl = '/customer/gis/agent/dashboard/summary';
      const url = agnCode
        ? `${baseUrl}?agn_code=${agnCode}`
        : `${baseUrl}?email=${encodeURIComponent(email)}`;
      const res = await axios.get(url, {
        validateStatus: () => true,
        timeout: 10000,
        _skipErrorRedirect: true,
      });
      if (res.status === 200 && res.data?.agent?.code != null) {
        const parsed = parseAgentNumericCode(res.data.agent.code);
        if (parsed != null) {
          if (email) saveAgentId(email, parsed);
          return parsed;
        }
      }
    } catch (e) {
      // fall through — 0 is valid for DIRECT accounts
    }
    return 0;
  },
  listAgentQuotes: listTravelAgentQuotes,
};

export default agentTravelApi;
